import PoolImage from 'public/images/shipsticks/how-it-works-background.jpg'

import { FAQProps } from 'lib/components/FAQ/FAQ'
import { FeatureListProps } from 'lib/components/FeatureList/FeatureList'
import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { IconProps } from 'lib/components/Icon/Icon'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { TwoThreeFourItemProps } from 'lib/components/TwoThreeFour/TwoThreeFourItem'
import { IconNames } from 'lib/settings/icons'

import InfoButton from '@/components/InfoButton'

export interface HowItWorksPageContent {
  hero: NarrativeHeroProps
  steps: { header?: string; steps: string[] }
  fifties: FiftyFiftyProps[]
  breaker: string
  feature: FeatureListProps
  videos: {
    header: string | React.ReactNode
    cards: TwoThreeFourItemProps[]
  }
  informationCards: InformationCardProps[]
  faq: FAQProps
}

export interface FiftyFiftyTextFields {
  header: string | React.ReactNode
  subHeader?: string | React.ReactNode
  image: string
  body: React.ReactNode
  eyebrow?: {
    icon: IconProps
    text: string
  }
  button?: {
    text: string
    link?: {
      href: string
    }
    action?: 'booking-widget-redirect'
  }
  showEyebrow?: boolean
}

export interface VideoProps {
  header: string | React.ReactNode
  cards: TwoThreeFourItemProps[]
}

export interface InformationCardProps {
  title: string
  description: string
  link: string
}

const BACKGROUND_IMAGE = typeof PoolImage === 'string' ? PoolImage : PoolImage.src

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: (
    <>
      How to ship golf clubs
      <br />
      (the easy way)
    </>
  ),
  subHeader: 'Skip baggage claim by shipping your golf clubs and luggage right to your destination',
}

// const steps: string[] = ['Book', 'Ship', 'Play']

const steps: { header?: string; steps: string[] } = {
  header: 'Learn how to ship golf clubs in three easy steps',
  steps: ['Book', 'Ship', 'Play'],
}

const fiftyTexts: FiftyFiftyTextFields[] = [
  {
    header: (
      <>
        1. Schedule your <strong>shipment.</strong>
      </>
    ),
    image: '/images/shipsticks/how-it-works-1.jpg',
    body: (
      <>
        <p className="leading-relaxed">
          Easily book online, through the app, or over the phone. We have more than 3,800 golf
          partners and can ship to just about anywhere in the world.
          <InfoButton
            tooltipText={
              'Choose one way, round-trip, or even multiple stops. Pro tip: Check that your ' +
              'destination can accept shipments.'
            }
            className="ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-1-circle' as IconNames,
      },
      text: 'Book',
    },
    button: {
      text: 'Get started',
      action: 'booking-widget-redirect',
    },
    showEyebrow: false,
  },
  {
    header: (
      <>
        2. Pack up your <strong>clubs.</strong>
      </>
    ),
    image: '/images/shipsticks/how-it-works-2.jpg',
    body: (
      <>
        <p className="leading-relaxed">
          Pack as you would for a flight and attach your provided label. We&apos;ll pick your clubs
          up at your home, office or even the course. You can also drop them off with the designated
          local carrier if you prefer.
          <InfoButton
            tooltipText={`Print your label from your confirmation email or from your account, or 
              email it to the local store of the carrier (FedEx, UPS, or DHL) to print`}
            className="ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-2-circle' as IconNames,
      },
      text: 'Ship',
    },
    button: {
      text: 'How to pack',
      link: {
        href: '/pack',
      },
    },
    showEyebrow: false,
  },
  {
    header: (
      <>
        3. Meet your clubs at your <strong>destination.</strong>
      </>
    ),
    image: '/images/shipsticks/how-it-works-3.jpg',
    body: (
      <>
        <p className="leading-relaxed">
          Enjoy flying (for a change) as you breeze by the baggage lines. Complimentary insurance,
          tracking, and an on-time guarantee let you enjoy relaxed travel from the get-go.
          <InfoButton
            tooltipText={
              "You'll usually find your clubs waiting for you at the golf shop or bag room - and " +
              'sometimes at the first tee! For hotels and resorts, check with the concierge'
            }
            className="ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-3-circle' as IconNames,
      },
      text: 'Play',
    },
    button: {
      text: 'On-time guarantee',
      link: {
        href: '/guarantee',
      },
    },
    showEyebrow: false,
  },
]

const fifties: FiftyFiftyProps[] = fiftyTexts.map((text, index) => ({
  ...text,
  image: {
    src: text.image,
  },
  layout: index % 2 === 0 ? 'image-right' : 'image-left',
  backgroundColor: 'white',
  className: 'md:gap-6 lg:gap-12',
}))

const breaker: string = `
  We’re here to make golf travel easier, more reliable, and way less stressful.
  That’s why every shipment is backed by the ShipSticks guarantee.
`

const feature: FeatureListProps = {
  header: 'Our guarantee',
  className: 'py-12',
  items: [
    {
      icon: 'clock',
      header: 'On-time guarantee',
      body: (
        <>
          It’s easy to ship your golf clubs with Ship Sticks. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '/guarantee',
        },
      },
    },
    {
      icon: 'shield-checkmark',
      header: 'Complimentary coverage',
      body: (
        <>
          It’s easy to ship your golf clubs with Ship Sticks. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      icon: 'customer-service',
      header: 'Customer support',
      body: (
        <>
          It’s easy to ship your golf clubs with Ship Sticks. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      icon: 'delivery-truck',
      header: 'Door-to-door service',
      body: (
        <>
          It’s easy to ship your golf clubs with Ship Sticks. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
  ],
}

const videos: VideoProps = {
  header: (
    <>
      Watch our videos <strong>to learn more</strong>
    </>
  ),
  cards: [
    {
      image: {
        src: 'https://img.youtube.com/vi/1Jh3gaZFBfk/maxresdefault.jpg',
      },
      header: 'Work and Wander: Make Business Travel Effortless with Luggage Shipping',
      body: (
        <>
          Business travel is an integral part of many professionals’ lives, offering opportunities
          for networking, attending conferences, and expanding their company’s reach. However,
          frequent business trips can quickly become a logistical challenge, especially when
          managing heavy luggage and navigating through crowded airports. Thankfully, our luggage
          shipping solutions can make business travel effortless. Explore how utilizing luggage...
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      video: 'https://img.youtube.com/vi/1Jh3gaZFBfk/maxresdefault.jpg',
      header: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
      body: (
        <>
          Donec ut diam nisi. Vivamus dapibus eros ac ante congue aliquet. Nulla facilisi. Integer
          eget mi libero. Nam id iaculis mauris. Proin vel est et nisi volutpat laoreet ornare vitae
          nunc. Sed rutrum sodales sem ac ultricies. Etiam eleifend, erat id ultrices fermentum,
          odio lacus faucibus nulla, at tincidunt dolor felis sed odio. Fusce mollis odio eu
          bibendum accumsan. Proin eget sodales tortor. Maecenas interdum orci mauris. Duis sit amet
          tincidunt diam.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      image: {
        src: 'https://img.youtube.com/vi/1Jh3gaZFBfk/maxresdefault.jpg',
      },
      header: 'Orci varius natoque penatibus et magnis dis parturient montes nascetur ridiculus',
      body: (
        <>
          Donec quis eleifend turpis, id convallis turpis. Integer pellentesque eros eu felis
          vehicula pharetra. Etiam ultrices, dui vitae venenatis sollicitudin, nunc quam molestie
          nisl, vel aliquet quam nunc sit amet tellus. Pellentesque sit amet tempus massa.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      image: {
        src: 'https://img.youtube.com/vi/1Jh3gaZFBfk/maxresdefault.jpg',
      },
      header: 'Nullam lobortis nisi lacus, at tincidunt ex porta',
      body: (
        <>
          Donec quis eleifend turpis, id convallis turpis. Integer pellentesque eros eu felis
          vehicula pharetra. Etiam ultrices, dui vitae venenatis sollicitudin, nunc quam molestie
          nisl, vel aliquet quam nunc sit amet tellus. Pellentesque sit amet tempus massa.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      video: 'https://img.youtube.com/vi/KKUCgDL-MmA/maxresdefault.jpg',
      header: 'Mauris ornare et ante sit amet sollicitudin',
      body: (
        <>
          Donec quis eleifend turpis, id convallis turpis. Integer pellentesque eros eu felis
          vehicula pharetra. Etiam ultrices, dui vitae venenatis sollicitudin, nunc quam molestie
          nisl, vel aliquet quam nunc sit amet tellus. Pellentesque sit amet tempus massa.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
  ],
}

const informationCards: InformationCardProps[] = [
  {
    title: 'What can I ship?',
    description:
      'Ship your luggage, boxes, trunks, golf clubs, skis, or snowboard to your destination.',
    link: '/what-can-i-ship',
  },
  {
    title: 'Packing guides',
    description:
      'Read step-by-step instructions on how to package your items and securely attach ' +
      'the shipping label.',
    link: '/pack',
  },
  {
    title: 'Our guarantee',
    description: 'Affordable pricing, on-time delivery, and the best service are guaranteed.',
    link: '/guarantee',
  },
]

const faq: FAQProps = {
  subtitle: 'Please choose a topic below to better pinpoint your question you may have.',
  title: (
    <>
      Have questions?
      <br />
      <b>We have answers.</b>
    </>
  ),
  categories: [
    {
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      categoryName: 'Packing & Preparation',
      categoryId: 4,
    },
    {
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
  ],
  items: [
    {
      question: 'What is ShipSticks?',
      answer: (
        <>
          <p>
            ShipSticks is a door-to-door shipping service that helps you travel more easily by
            sending your golf clubs, luggage, and other gear ahead of your trip. We pick up from
            your home, office, golf course, or hotel and deliver directly to your destination.
          </p>

          <p>
            By shipping ahead with ShipSticks, you can skip the stress of hauling heavy bags through
            airports, waiting at baggage claim, and dealing with lost or delayed luggage.
          </p>
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'How do I create an account?',
      answer: (
        <>
          Navigate to &apos;Log In&apos; in the top right corner of your screen. You can either
          create a new account or sign in to an existing one, but you will need one to book.
          Creating an account requires no commitment and only takes a few moments to complete.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'Is it free to create an account?',
      answer: (
        <>
          Yes, signing up is completely free! There&apos;s no cost or commitment to create an
          account.
        </>
      ),
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      question: 'Who can use ShipSticks?',
      answer: (
        <>
          Anyone can use ShipSticks! Whether you&apos;re a seasoned golfer, a first-time golf
          traveler, or shipping on behalf of someone else, our service is designed to make travel
          easier for everyone.
        </>
      ),
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      question: 'How do I book a shipment?',
      answer: (
        <>
          Click here to get started. Simply enter your pickup and delivery locations, select your
          travel dates, and choose the service that best fits your needs.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'What happens after I book?',
      answer: (
        <>
          Once you complete your booking, you&apos;ll receive your shipping labels and step-by-step
          instructions.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'How many days in advance should I schedule my gear to arrive?',
      answer: (
        <>
          We suggest scheduling your gear to arrive at least one business day before your first
          round. For example, if your tee time is on Monday, plan to have your clubs delivered by
          the Friday before.
        </>
      ),
    },
    {
      question: 'What if my local club isn’t a ShipSticks partner?',
      answer: (
        <>
          That’s ok! You can still use ShipSticks, even if your local club isn’t an official
          partner. We do, however, recommend suggesting the service to your club pro, so you can
          start enjoying the convenience of shipping directly from your local pro shop.
        </>
      ),
    },
    {
      question: 'What’s the difference between one-way and round-trip shipping?',
      answer: (
        <>
          One-way shipping means your golf clubs or gear are shipped from your starting point to
          your destination. Round-trip shipping includes shipping your gear to your destination and
          then back to your home or original location. There is no cost difference between two
          one-way shipments and a round-trip booking.
        </>
      ),
    },
    {
      question: 'What other items can I ship using ShipSticks?',
      answer: (
        <>
          <p>
            Travel completely bag-free by shipping your luggage along with your golf clubs. We can
            accommodate carry-on, checked, and oversized luggage.
          </p>
          <p>
            If you’re interested in shipping specialty items like push carts, or arranging group or
            event shipments, please contact our team so we can explore how to best accommodate your
            needs.
          </p>
        </>
      ),
    },
    {
      question: 'Where can I ship my gear?',
      answer: (
        <>
          You can send your shipment to any physical destination of your choosing, with the
          exception of P.O. boxes, airports, and countries we don’t currently service.
        </>
      ),
    },
    {
      question: 'What types of bags/containers are allowed?',
      answer: (
        <>
          Pack your golf clubs just like you would for the airlines, in a soft travel bag or hard
          case. You may also package your clubs in a box, but it is not required. Luggage should be
          packed in a suitcase, duffel bag, or box.
        </>
      ),
    },
    {
      question: 'How should I pack my clubs or luggage?',
      answer: (
        <>
          Click <a href="/pack">here</a> for more information on packing golf clubs and luggage.
        </>
      ),
    },
    {
      question: 'Can I lock my domestic shipment?',
      answer: (
        <>
          If you’re shipping domestically within the U.S., your bag may be locked. However, we
          recommend leaving it unlocked to avoid any potential delays.
        </>
      ),
    },
    {
      question: 'Can I lock my international shipment?',
      answer: (
        <>
          No, you cannot lock your bag when shipping internationally. All bags must be unlocked to
          ensure customs inspections can be performed if necessary.
        </>
      ),
    },
    {
      question: 'Are there size or weight limits?',
      answer: (
        <>
          Yes. Please identify your bag’s maximum dimensions (length + width + height) and weight to
          determine which bag size category it fits into. If your shipment exceeds the size or
          weight limits for that category, please{' '}
          <a href="https://www.shipsticks.com/contact">contact our team</a> - we’ll be happy to
          provide a custom quote for your shipment. Examples: If you have a suitcase that weighs 24
          lbs with a maximum dimension of 60 inches, it should be booked as Checked Luggage. If you
          have a golf bag that weighs 55 lbs with a maximum dimension of 70 inches, it should be
          booked as an XL Golf Bag. Bag Type Maximum Dimension Maximum Weight Standard Golf Bag 72
          inches 42 lbs XL Golf Bag 79 inches 56 lbs Carry-on Luggage 45 inches 25 lbs Checked
          Luggage 62 inches 50 lbs Oversized Luggage 75 inches 65 lbs
        </>
      ),
    },
    {
      question: 'What happens if my bag is overweight?',
      answer: (
        <>
          If your bag weighs more than the limit for your chosen size, there could be extra fees and
          possible delays. To keep things running smoothly, we recommend sizing up if you’re unsure
          - better safe than sorry!
        </>
      ),
    },

    {
      question: 'What if I can’t weigh my bag?',
      answer: (
        <>
          Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your
          bag weighed. If the weight is significantly heavier than expected, additional fees may
          apply.
        </>
      ),
    },
    {
      question: 'How do I attach the shipping labels?',
      answer: (
        <>
          If you&apos;re using a hard travel case or box, attach your label with clear packing tape,
          making sure the barcode is visible and secure. If your bag doesn&apos;t have a hard
          surface, you&apos;ll need to place the label on a luggage baggage tag (also known as a fly
          tag), which you can pick up from your local FedEx, UPS, or DHL location, and attach it to
          the handle. Don&apos;t forget to put a second copy of your shipping label in your bag,
          too.
        </>
      ),
      categoryName: 'Packing & Preparation',
      categoryId: 4,
    },
    {
      question: 'What if I don’t have a printer?',
      answer: (
        <>
          You can email the shipping label to your local FedEx, UPS, or DHL for printing. Or, if
          time allows, for an additional fee we can mail printed labels directly to you - just give
          us a heads-up when placing your order.
        </>
      ),
    },
    {
      question: 'Does ShipSticks provide packaging?',
      answer: (
        <>
          We don’t supply packaging, but no worries - you can simply use your own hard case, soft
          travel bag, or box to ship your gear.
        </>
      ),
    },
    {
      question: 'Do I need a box?',
      answer: (
        <>
          Nope, no box needed! If you’re shipping a golf bag in an airline-approved soft travel bag
          or hard case, you’re good to go. Same goes for luggage - as long as it’s packed in a
          suitcase or duffle bag.
        </>
      ),
    },
    {
      question: 'Where do I get a box?',
      answer: (
        <>
          If you have a soft travel bag or hard case, you don’t need a box - that’s the beauty of
          our service. However, if you need a box, you can usually buy one at your local UPS or
          FedEx store for a small fee (prices vary by location). We also sell golf bag-sized boxes -
          just reach out to Customer Support to order. Here’s our pricing and shipping times for
          boxes:
          <br />
          Ground Shipping (5–7 Business Days)
          <br />
          1 Box: $29.99
          <br />
          2 Boxes: $34.99
          <br />
          Express Shipping (1–3 Business Days)
          <br />
          1 Box: $39.99
          <br />2 Boxes: $44.99
        </>
      ),
    },
    {
      question: 'Do I have to pack my bag differently if I am shipping internationally?',
      answer: (
        <>
          For international shipments, no locks are allowed, including TSA locks, since customs may
          need to inspect your package.
          <br />
          Here’s what you can include in your golf bag: clubs, bags, balls, golf shoes, and rain
          gear like umbrellas or jackets and pants. For luggage, you can pack worn clothing, new
          clothing with price tags removed, footwear, headwear, and undergarments.
        </>
      ),
    },
    {
      question: 'What can I include in my shipment?',
      answer: (
        <>
          Here’s what you can include in your golf bag: clubs, bags, balls, golf shoes, and rain
          gear like umbrellas or jackets and pants. For luggage, you can pack worn clothing, new
          clothing with price tags removed, footwear, headwear, and undergarments.
        </>
      ),
    },
    {
      question: 'What items are prohibited?',
      answer: (
        <>
          Some things are off-limits, like toiletries (soap, shampoo, toothpaste), medications,
          pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable items,
          electronics like rangefinders and GPS devices, batteries, alcohol or anything containing
          alcohol (including perfume and cologne), tobacco, firearms and ammunition, and food,
          vitamins, or other consumables.
          <br />
          Before shipping, please remove all price tags. For the full list of prohibited items,
          check out our Terms of Use.
        </>
      ),
    },
    {
      question: 'Can I add luggage to my order?',
      answer: (
        <>
          Yes! Adding pieces of luggage to your order is simple. Just scroll down to the traveler
          section, below the pickup and delivery dates, and you’ll see an option labeled Luggage
          Count. From the dropdown menu, select the number of pieces you want to ship and choose the
          type—carry-on, checked, or oversized bag.
        </>
      ),
    },
    {
      question: 'Can I add hotel check-in information to my shipping label?',
      answer: (
        <>
          Yes, under ‘Other Info’ on the booking page, you can type in any information you would
          like. For example: Confirmation Number XXXXX, Checking in XX/XX/XX, Guest of John Smith,
          etc.
        </>
      ),
    },
    {
      question: 'Who will be picking up my shipment?',
      answer: (
        <>
          One of our trusted logistics partners, either FedEx, UPS, or DHL, will pick up your clubs
          or luggage. Your shipping label will indicate which carrier will handle your pickup.
        </>
      ),
    },
    {
      question: 'Can I get my golf clubs picked up from my home?',
      answer: (
        <>
          Yes! We can pick your clubs up from your home, office, or golf course - or you can simply
          drop them off at your nearest carrier store for added convenience.
        </>
      ),
    },
    {
      question: 'Is there a fee for dropping off my golf clubs?',
      answer: (
        <>
          FedEx Offices and UPS Stores do not charge fees to drop off. However, some third-party
          pickup and drop-off locations may charge package handling fees. These fees are separate
          from our service and can vary depending on the location. To avoid surprises, we recommend
          calling ahead to check if any fees apply.
        </>
      ),
    },
    {
      question: 'Can I choose my carrier drop-off location?',
      answer: (
        <>
          While we assign your carrier (FedEx, UPS, or DHL) when you book, you’re free to drop off
          your shipment at any local location serviced by that carrier. Click here to find a
          drop-off location near you.
        </>
      ),
    },
    {
      question: 'Do I have to be home when my bags are picked up?',
      answer: (
        <>
          You don’t need to be home for pickup. Just leave your securely packed and labeled shipment
          outside your front door during the pickup window, at your discretion. If you don’t want to
          leave it unattended and can’t be home, you can drop it off at a local FedEx, UPS, or DHL
          store - just make sure it matches the carrier listed on your shipping label.
        </>
      ),
    },
    {
      question: 'Can I cancel my order?',
      answer: (
        <>
          Yes, you may cancel your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          cancel.
        </>
      ),
    },
    {
      question: 'Can I make changes to my order?',
      answer: (
        <>
          Yes! You can update your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          modify. Just make sure to do it before your shipment is in transit. Once it’s on the move,
          you’ll need to contact customer support to request changes, and fees may apply.
        </>
      ),
    },
    {
      question: 'How long does delivery take?',
      answer: (
        <>
          Delivery times vary depending on the service level you choose. Ground shipments typically
          arrive within 2 to 6 business days, depending on the route, while expedited options can
          get your gear there even faster - some even as quickly as the next business day. After
          pickup, you’ll receive tracking updates so you can monitor your shipment every step of the
          way.
        </>
      ),
    },
    {
      question: 'Is a signature required upon delivery?',
      answer: (
        <>
          A signature is not required, but it’s ultimately up to the driver’s discretion. If the
          driver considers the delivery area secure or your destination has a shipping/receiving
          area, your shipment will be delivered without a signature. If this is a concern, we
          recommend shipping to a supervised location, like a business, golf course, resort, friend
          or family member, or carrier store (fees may apply).
        </>
      ),
    },
    {
      question: 'Does ShipSticks deliver on weekends?',
      answer: (
        <>
          Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled
          during regular business days when all of our partnered carriers are operating. That said,
          depending on the route, your gear might still arrive on a weekend.
        </>
      ),
    },
    {
      question:
        'Do I need to confirm that the golf course, resort, ' +
        'rental home, or hotel will accept my shipment?',
      answer: (
        <>
          Before placing your order, we do recommend reaching out to your destination to let them
          know about your shipment and your expected arrival date.
        </>
      ),
    },
    {
      question: 'Do hotels and golf courses charge to receive/hold my gear?',
      answer: (
        <>
          Policies vary by location. Some properties may charge a fee for receiving or holding
          shipments, so it’s best to check directly with them beforehand.
        </>
      ),
    },
    {
      question:
        'Can I have my bag returned to a different address than the one ' +
        'it was originally shipped from?',
      answer: (
        <>
          That’s easy! Just book two separate one-way shipments - one to send your bag out, and
          another to have it returned to a different address. Don’t worry, the cost is the same as a
          round-trip shipment. This also works if your trip has multiple stops and you need your bag
          sent to different places along the way.
        </>
      ),
    },
    {
      question: 'How do I track my shipment?',
      answer: (
        <>
          We&apos;ll keep you posted with email updates and optional text alerts when your shipment
          is picked up, out for delivery, and delivered. You&apos;ll also find a tracking link in
          your confirmation email. To track through your account, simply:
          <br />
          Visit www.ShipSticks.com
          <br />
          Log in (top-right corner)
          <br />
          Go to My Account {'>'} Shipments
          <br />
          Select the shipment you&apos;d like to track
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'Will I get notifications for delivery updates?',
      answer: (
        <>
          Yes! You’ll receive real-time tracking notifications via email so you always know where
          your shipment is and when to expect delivery. You can opt-in to receive notifications via
          text as well.
        </>
      ),
    },
    {
      question: 'What if my shipment is delayed or lost?',
      answer: (
        <>
          Although delays and losses are rare, if we suspect either might have occurred, our
          customer support team will reach out to you. We’ll promptly investigate and work to
          resolve the issue as quickly as possible.
        </>
      ),
    },
    {
      question: 'Do you ship internationally?',
      answer: (
        <>We service 180 countries and territories, and handle all the tricky customs paperwork.</>
      ),
    },
    {
      question: 'How much does it cost to ship golf clubs?',
      answer: (
        <>
          The cost to ship golf clubs varies depending on factors like your shipping distance and
          the service level you choose. To get an exact quote, simply enter your pickup and delivery
          locations on our website or contact our customer support team for personalized assistance.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'Is insurance included? Can I add more coverage?',
      answer: (
        <>
          Domestic shipments come with $1,000 of insurance for golf bags and $500 for luggage.
          International shipments include $500 for either. Need more coverage? You can add extra for
          a small fee. We recommend choosing an amount that reflects the full value of your gear and
          the bag it’s packed in.
        </>
      ),
    },
    {
      question: 'What is covered under insurance?',
      answer: (
        <>
          Ship Sticks&apos; shipment insurance protects against complete loss of your shipped item
          or against any physical damage to qualified items that has occurred while in transit. For
          items not shipped in a box, the outside casing is considered the shipping container.
          Please note that general wear and tear, along with damage to protruding parts such as
          wheels, straps, pockets, handles, or other attachments on a travel case or bag, are not
          covered under this protection.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'How do I file a damage/missing item claim?',
      answer: (
        <>
          In order to file a damage or missing items claim, a{' '}
          <a href="https://www.shipsticks.com/claims">claims form</a> must be submitted to a claims
          representative. Claims requirements are outlined on our claims form.
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
    {
      question: 'How long does it take to resolve a claim?',
      answer: (
        <>
          <p>
            A claims specialist will review and file your claim within 1-2 business days. If
            additional information is needed, a claims specialist will contact you by phone or
            email. Otherwise, you will be made aware of your claim being filed.
          </p>
          <p>
            Once your claim has been reviewed and filed, you can expect to receive an approval or
            denial within 14 business days. Once the claim has been filed, it cannot be altered. All
            decisions determined by our 3rd party insurance carrier are final.
          </p>
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
    {
      question: 'Can I ship luggage, push carts, or oversized items?',
      answer: (
        <>
          Yes! You can ship luggage along with your golf clubs. For specialty items like push carts
          or oversized gear, just contact our team - we&apos;re happy to help arrange those
          shipments.
        </>
      ),
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      question: 'How do I get a custom quote for special shipments?',
      answer: (
        <>
          To get a custom quote for specialty or oversized shipments, please{' '}
          <a href="https://www.shipsticks.com/contact">contact</a> our customer support team with
          your shipment details. We&apos;ll work with you to provide a personalized price and
          shipping plan.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'Are there items you cannot ship?',
      answer: (
        <>
          Yes, there are certain items we cannot ship for safety and legal reasons. These include
          hazardous materials, perishable goods, and restricted items as outlined in our shipping
          policy. If you&apos;re unsure whether your item qualifies, please{' '}
          <a href="https://www.shipsticks.com/contact">contact</a> us before booking.
        </>
      ),
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      question: 'How do I contact customer support?',
      answer: (
        <>
          Click <a href="https://www.shipsticks.com/contact">here</a> to contact us.
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
  ],
  button: {
    moreText: 'View More FAQs',
  },
  variant: 'white',
}

const content: HowItWorksPageContent = {
  faq,
  hero,
  steps,
  fifties,
  breaker,
  feature,
  videos,
  informationCards,
}

export default content
